import { formatNumber } from 'utils/number';

/**
 * Reads a coordinate pair the way people copy it out of Google Maps:
 *
 *   31.968600, -102.077900          (right-click → the coordinates)
 *   31.9686 -102.0779
 *   31.9686° N, 102.0779° W
 *   31°58'07.0"N 102°04'40.4"W      (the place card's DMS line)
 *   31 58 7 N, 102 4 40.4 W
 *
 * Latitude comes first, as Google writes it. A hemisphere letter (N/S/E/W) sets the
 * sign and, when given, which number is which.
 */
export type LngLat = [number, number];

/**
 * A well, place or survey area the Location field can drop a pin on. A survey area's
 * search carries no shape, so it has `locate` to find its spot once picked.
 */
export type LocationSuggestion = {
  id: string;
  kind: 'well' | 'place' | 'surveyArea';
  title: string;
  detail: string;
} & (
  | { lngLat: LngLat; locate?: never }
  | { lngLat?: never; locate: () => Promise<LngLat | null> }
);

/**
 * Finds wells, places and survey areas matching the Location field. The app shell
 * supplies it, since components here don't fetch. Rejects when it couldn't search.
 */
export type SearchLocations = (
  text: string,
  near: LngLat | undefined,
  signal: AbortSignal
) => Promise<LocationSuggestion[]>;

export type ParsedCoordinates =
  { ok: true; lngLat: LngLat } | { ok: false; error: string };

interface Part {
  value: number;
  hemi: 'N' | 'S' | 'E' | 'W' | null;
}

const NUMBER = /-?(?:\d+(?:\.\d+)?|\.\d+)/g;
// A letter on its own, so the n in a "Long:" label isn't read as North.
const HEMISPHERE = /(?<![a-z])[NSEW](?![a-z])/i;
const HEMISPHERE_PAIR =
  /^(.*?(?<![a-z])[NSEW](?![a-z]))\s*(.+(?<![a-z])[NSEW](?![a-z]).*)$/i;
const LABEL = /(?<![a-z])(?:lat(?:itude)?|long(?:itude)?|lng|lon)(?![a-z])/gi;
const HEMISPHERE_LETTERS = new RegExp(HEMISPHERE.source, 'gi');

/**
 * Which axis a hemisphere letter belongs to.
 *
 * @param {Part['hemi']} hemi - N, S, E, W or null.
 * @return {'lat' | 'lng' | null} The axis, or null without a letter.
 */
const axisOf = (hemi: Part['hemi']): 'lat' | 'lng' | null => {
  if (hemi === 'N' || hemi === 'S') return 'lat';
  if (hemi === 'E' || hemi === 'W') return 'lng';
  return null;
};

/**
 * One coordinate: a signed decimal, or degrees [minutes [seconds]], plus an optional
 * hemisphere.
 *
 * @param {string} text - One half of the pair.
 * @return {Part | null} The signed value and its hemisphere, or null if unreadable.
 */
const readPart = (text: string): Part | null => {
  const hemiMatch = HEMISPHERE.exec(text);
  const hemi = hemiMatch ? (hemiMatch[0].toUpperCase() as Part['hemi']) : null;
  const nums = (text.match(NUMBER) ?? []).map(Number);
  if (nums.length === 0 || nums.length > 3) return null;
  const [d, m = 0, s = 0] = nums;
  if (nums.length > 1 && (m < 0 || m >= 60 || s < 0 || s >= 60)) return null;
  // "-0 30" is -0.5, and -0 isn't less than 0.
  const sign = d < 0 || Object.is(d, -0) ? -1 : 1;
  let value = sign * (Math.abs(d) + m / 60 + s / 3600);
  if (hemi === 'S' || hemi === 'W') value = -Math.abs(value);
  if (hemi === 'N' || hemi === 'E') value = Math.abs(value);
  return { value, hemi };
};

/**
 * Splits "lat, lng" / "lat lng" / DMS pairs into the two coordinate strings.
 *
 * @param {string} input - The raw pasted or typed text.
 * @return {[string, string] | null} The two halves, or null if it isn't a pair.
 */
const splitPair = (input: string): [string, string] | null => {
  const text = input
    .trim()
    // Typographic quotes and primes from copied DMS.
    .replace(/[′’‘]/g, "'")
    .replace(/[″“”]/g, '"')
    .replace(/º/g, '°')
    // Minus signs from documents and web pages, which would otherwise be dropped.
    .replace(/[−–—]/g, '-');
  // Any other word makes it a name to search for, such as "BLK-7, S-12" or "T20S R34E".
  if (/[a-z]/i.test(text.replace(LABEL, '').replace(HEMISPHERE_LETTERS, ''))) {
    return null;
  }
  if (text.includes(',')) {
    const parts = text.split(',');
    return parts.length === 2 ? [parts[0], parts[1]] : null;
  }
  // DMS or decimals with hemisphere letters: split after the first letter, whichever
  // axis it is. The letters then decide the order, or reject a same-axis pair.
  const hemi = HEMISPHERE_PAIR.exec(text);
  if (hemi) return [hemi[1], hemi[2]];
  // Two bare decimals separated by space. Letters that didn't make a pair above
  // would be dropped here, flipping a sign without a word. Whole numbers alone, or
  // joined by a hyphen, are more likely a section or abstract ("37-12") than a spot.
  const bare = /^(\S+)\s+(\S+)$/.exec(
    text.replace(LABEL, ' ').replace(/:/g, ' ').trim()
  );
  if (
    bare &&
    bare.slice(1).every((half) => /^-?(?:\d+(?:\.\d+)?|\.\d+)$/.test(half)) &&
    bare.slice(1).some((half) => half.includes('.')) &&
    !HEMISPHERE.test(text)
  ) {
    return [bare[1], bare[2]];
  }
  return null;
};

/**
 * Parses a latitude/longitude pair.
 *
 * @param {string} input - Decimal or degrees/minutes/seconds, latitude first.
 * @return {ParsedCoordinates} `[lng, lat]` for the map, or an error to show the user.
 */
export const parseCoordinates = (input: string): ParsedCoordinates => {
  if (!input.trim()) {
    return { ok: false, error: 'Enter a latitude and longitude.' };
  }
  const pair = splitPair(input);
  const a = pair && readPart(pair[0]);
  const b = pair && readPart(pair[1]);
  if (!a || !b) {
    return {
      ok: false,
      error:
        'Use latitude, longitude — e.g. 31.9686, -102.0779 or 31°58\'07"N 102°04\'40"W.',
    };
  }
  if (axisOf(a.hemi) && axisOf(a.hemi) === axisOf(b.hemi)) {
    return {
      ok: false,
      error: 'Give one latitude (N or S) and one longitude (E or W).',
    };
  }
  // Latitude first unless the hemisphere letters say otherwise.
  let lat = a.value;
  let lng = b.value;
  if (axisOf(a.hemi) === 'lng' && axisOf(b.hemi) === 'lat') {
    lat = b.value;
    lng = a.value;
  }
  if (Math.abs(lat) > 90) {
    return { ok: false, error: 'Latitude must be between -90 and 90.' };
  }
  if (Math.abs(lng) > 180) {
    return { ok: false, error: 'Longitude must be between -180 and 180.' };
  }
  return { ok: true, lngLat: [lng, lat] };
};

/**
 * A spot from a map event, with the longitude brought back into -180..180. Zoomed
 * out, the map repeats the world and a click on a copy reads e.g. 257.92.
 *
 * @param {{ lng: number; lat: number }} lngLat - The event's spot.
 * @return {LngLat} `[lng, lat]`.
 */
export const wrapLngLat = ({
  lng,
  lat,
}: {
  lng: number;
  lat: number;
}): LngLat => {
  if (Math.abs(lng) <= 180) return [lng, lat];
  const wrapped = ((((lng + 180) % 360) + 360) % 360) - 180;
  return [wrapped === -180 ? 180 : wrapped, lat];
};

/**
 * A coordinate to six places with no grouping, so it pastes back into Google Maps.
 *
 * @param {number} value - Degrees.
 * @return {string} e.g. "-102.077900".
 */
const sixPlaces = (value: number): string =>
  formatNumber(value, 6, 6, 'decimal', false);

/**
 * Google's own "lat, lng" form, the one its search box takes.
 *
 * @param {LngLat} lngLat - The spot, `[lng, lat]`.
 * @return {string} e.g. "31.968600, -102.077900".
 */
export const formatDecimalPair = ([lng, lat]: LngLat): string =>
  `${sixPlaces(lat)}, ${sixPlaces(lng)}`;

/**
 * Google Maps' documented search URL for the spot.
 *
 * @param {LngLat} lngLat - The spot, `[lng, lat]`.
 * @return {string} A URL that opens Google Maps centred on the spot.
 */
export const googleMapsUrl = ([lng, lat]: LngLat): string =>
  `https://www.google.com/maps/search/?api=1&query=${sixPlaces(lat)},${sixPlaces(lng)}`;

/**
 * The DOM id of a suggestion, for the field's active descendant.
 *
 * @param {string} listId - The suggestion list's id.
 * @param {LocationSuggestion} suggestion - The suggestion.
 * @return {string} Its option id.
 */
export const optionId = (listId: string, suggestion: LocationSuggestion) =>
  `${listId}-${suggestion.id}`;
